import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Users2, Settings, ShieldCheck, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/brand/Logo";
import OrgSwitcher from "@/components/dashboard/OrgSwitcher";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/team", label: "Team", icon: Users2 },
  { to: "/settings", label: "Settings", icon: Settings },
];

function SidebarContent({ user, logout, onNav }) {
  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10">
        <LogoMark className="h-8 w-8" />
        <span className="font-display font-extrabold text-lg text-white">Pitso<span className="text-accent">IO</span></span>
      </div>

      <div className="px-3 pt-4"><OrgSwitcher /></div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`sidebar-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
        {user?.is_super_admin && (
          <NavLink to="/super-admin" onClick={onNav} data-testid="sidebar-super-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors mt-2 ${isActive ? "bg-accent/20 text-accent" : "text-accent/80 hover:text-accent hover:bg-accent/10"}`}>
            <ShieldCheck className="h-4 w-4" /> Super Admin
          </NavLink>
        )}
      </nav>

      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 mb-1">
          <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
          <p className="text-xs text-white/50 truncate">{user?.email}</p>
        </div>
        <button onClick={logout} data-testid="sidebar-logout"
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 bg-primary flex-col z-40">
        <SidebarContent user={user} logout={logout} />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-primary">
            <button className="absolute top-4 right-4 text-white/70" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
            <SidebarContent user={user} logout={logout} onNav={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-60 min-w-0">
        <div className="lg:hidden h-14 border-b border-border flex items-center justify-between px-4">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
          <span className="font-display font-bold">Pitso<span className="text-accent">IO</span></span>
          <div className="w-5" />
        </div>
        <main className="min-h-screen"><Outlet /></main>
      </div>
    </div>
  );
}
